/**
 * session 轉送到 Worker 的橋。
 *
 * 為什麼不能只靠 cookie：console（`chat-rea-console.pages.dev`）打的是
 * `chat-rea-proxy.<acct>.workers.dev`，而 `vm_session` cookie 的 host 是
 * `vm-auth-service.<acct>.workers.dev`——`workers.dev` 是 public suffix，
 * cookie 只能是 host-only，瀏覽器**不會**把這顆 cookie 送到另一個 worker 網域。
 * 所以前端把 `currentSessionId`（就是 vm_session 的值）放在 `x-vm-session` 標頭，
 * Worker 收到後再以 `Cookie: vm_session=...` 轉去 `GET /auth/session` 驗。
 *
 * 代價：session id 因此對 JS 可見（原本 HttpOnly 的好處只在 auth-service 自己的網域成立）。
 * 這是跨 host 驗證的取捨，已寫進 docs/DECISIONS.md。
 *
 * 存在模組記憶體、不落地：重新整理就重新問一次 auth-service（過期即回登入頁）。
 */
let sessionId: string | null = null;
let roles: readonly string[] = [];
/** PIN 登入簽發的 Bearer token；只存在 sessionStorage（分頁關掉即消失）。 */
const PIN_TOKEN_KEY = 'chat-rea.pin-token';
let pinToken: string | null = readStoredToken();

function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(PIN_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setPinToken(token: string | null): void {
  pinToken = token;
  try {
    if (token === null) sessionStorage.removeItem(PIN_TOKEN_KEY);
    else sessionStorage.setItem(PIN_TOKEN_KEY, token);
  } catch {
    // 無痕模式或 storage 被拒：仍用模組記憶體，重新整理後就要重新登入。
  }
}

export function getPinToken(): string | null {
  return pinToken ?? readStoredToken();
}

export function setVmSession(next: { id: string; roles: readonly string[] } | null): void {
  sessionId = next?.id ?? null;
  roles = next?.roles ?? [];
}

export function getVmSessionId(): string | null {
  return sessionId;
}

export function isAdminSession(): boolean {
  return roles.includes('admin');
}

/** 給 http client：每次請求即時讀，避免 client 建立時 session 還沒拿到。 */
export function vmSessionHeaders(): Record<string, string> {
  const token = getPinToken();
  if (token !== null) return { authorization: `Bearer ${token}` };
  return sessionId === null ? {} : { 'x-vm-session': sessionId };
}
